/**
 * Пакеты проверки в браузере: получение у сервера (с токеном игрока), сохранение файлов,
 * независимая проверка тем же кодом, что и консольная утилита (@mt/verification).
 */
import { formatNote, verifyPackage, type SignedPackage, type VerifyNote, type VerifyReport } from '@mt/verification';
import type { Msg } from '@mt/protocol';
import { credits, getLang, locale, num, t, tm, type TKey } from '../i18n';
import { authToken } from '../net/client';

export async function fetchPackage(gameId: string): Promise<{ signed: SignedPackage; text: string }> {
  const token = authToken();
  const res = await fetch(`/api/games/${encodeURIComponent(gameId)}/verification`, { headers: token ? { authorization: `Bearer ${token}` } : {} });
  const text = await res.text();
  if (!res.ok) {
    let msg = t('verify.httpError', { status: res.status });
    try {
      const body = JSON.parse(text) as { message?: string; msg?: Msg };
      msg = tm(body.msg, body.message ?? msg);
    } catch {
      /* не JSON */
    }
    throw new Error(msg);
  }
  return { signed: JSON.parse(text) as SignedPackage, text };
}

let cachedKey: string | null = null;
/** Открытый ключ сервера; проверка сверяет с ним ключ в файле. */
export async function serverPublicKey(): Promise<string | undefined> {
  if (cachedKey) return cachedKey;
  try {
    cachedKey = ((await (await fetch('/api/verification-key')).json()) as { publicKey: string }).publicKey;
    return cachedKey;
  } catch {
    return undefined;
  }
}

export async function verify(signed: unknown): Promise<VerifyReport> {
  return verifyPackage(signed, { trustedPublicKey: await serverPublicKey() });
}

/** Порядок форм множественного числа в шаблонах проверок (как в tp()). */
const PLURAL_FORMS: Record<string, Intl.LDMLPluralRule[]> = { ru: ['one', 'few', 'many'], en: ['one', 'other'], de: ['one', 'other'] };

/** Строка результата проверки (код с параметрами) на языке пользователя. */
/** Источник данных на языке интерфейса (в пакете — описание data-api по-русски, оно остаётся в подсказке). */
export function sourceLabel(source: string, description: string): string {
  if (source === 'synthetic') return t('verify.sourceSynthetic');
  if (source === 'alpaca') return t('verify.sourceAlpaca', { feed: /\b(IEX|SIP)\b/i.exec(description)?.[1]?.toUpperCase() ?? 'IEX' });
  return description;
}

export function checkText(n: VerifyNote): string {
  // строка об источнике котировок: описание data-api заменяется подписью на языке интерфейса
  const kind = n.params?.kind;
  if (n.code === 'aggregate.source' && typeof kind === 'string') n = { ...n, params: { ...n.params, source: sourceLabel(kind, String(n.params?.source ?? '')) } };
  return formatNote(n, {
    template: (code) => t(`verify.check.${code}` as TKey),
    credits,
    num,
    plural: (x, forms) => {
      const order = PLURAL_FORMS[getLang()] ?? ['one', 'other'];
      const i = order.indexOf(new Intl.PluralRules(locale()).select(x));
      return forms[Math.min(i < 0 ? forms.length - 1 : i, forms.length - 1)] ?? '';
    },
    srv: (m) => tm(m),
  });
}

export function saveFile(name: string, content: string, mime: string): void {
  const url = URL.createObjectURL(new Blob([content], { type: mime }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1_000);
}

export async function downloadPackage(gameId: string): Promise<SignedPackage> {
  const { signed, text } = await fetchPackage(gameId);
  saveFile(`market-table-${gameId}.json`, text, 'application/json');
  return signed;
}
